import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import { getAll } from '../../services/api';
import { notify } from '../../utils/notifications';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Users, Bus,
  ArrowRight, ArrowLeft, X, Clock, CheckCircle, XCircle, MapPin,
  UserCircle, School, RotateCcw, CheckSquare, Loader2, AlertTriangle, Merge
} from 'lucide-react';

const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];
const GIORNI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const iso = (d) => {
  // Non si può usare toISOString: converte in UTC e di notte sposta il giorno
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const g = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${g}`;
};

const ETICHETTE_MOTIVO = {
  VALIDO: 'Valido',
  SCADUTO: 'Abbonamento scaduto',
  SOSPESO: 'Abbonamento sospeso',
  GRUPPO_NON_CORRISPONDENTE: 'Gruppo non corrispondente',
  ABBONAMENTO_NON_ANCORA_VALIDO: 'Non ancora valido',
  IN_ATTESA_PAGAMENTO: 'In attesa di pagamento',
  ANAGRAFICA_INCOMPLETA: 'Anagrafica incompleta',
  MENSILITA_NON_SALDATA: 'Mensilità non saldata',
  NON_TROVATO: 'Tesserino non riconosciuto',
};

const StoricoCorse = () => {
  const { utente } = authStore();
  const modificabile = puoModificare(utente);
  const oggi = new Date();
  const [mese, setMese] = useState(new Date(oggi.getFullYear(), oggi.getMonth(), 1));
  const [giorni, setGiorni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [giornoScelto, setGiornoScelto] = useState(null);
  const [corseGiorno, setCorseGiorno] = useState([]);
  const [caricamentoGiorno, setCaricamentoGiorno] = useState(false);
  const [dettaglio, setDettaglio] = useState(null);
  const [caricamentoDettaglio, setCaricamentoDettaglio] = useState(false);
  const [inChiusura, setInChiusura] = useState(null);
  const [duplicati, setDuplicati] = useState(null);

  const primoGiorno = new Date(mese.getFullYear(), mese.getMonth(), 1);
  const ultimoGiorno = new Date(mese.getFullYear(), mese.getMonth() + 1, 0);

  const caricaMese = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/trasporti/corse/calendario/', {
        params: { data_da: iso(primoGiorno), data_a: iso(ultimoGiorno) }
      });
      setGiorni(res.data || []);
    } catch (error) {
      console.error('Errore caricamento calendario:', error);
      notify.error('Errore nel caricamento dello storico');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mese]);

  useEffect(() => {
    caricaMese();
    setGiornoScelto(null);
    setCorseGiorno([]);
  }, [caricaMese]);

  // I viaggi spezzati in più partenze non dipendono dal mese mostrato
  const contaDuplicati = useCallback(async () => {
    if (!modificabile) return;
    try {
      const res = await api.get('/trasporti/corse/duplicati/');
      setDuplicati(res.data);
    } catch (error) {
      console.error('Errore conteggio duplicati:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modificabile]);

  useEffect(() => {
    contaDuplicati();
  }, [contaDuplicati]);

  const caricaCorseGiorno = async (dataISO) => {
    if (!dataISO) return;
    setCaricamentoGiorno(true);
    try {
      const corse = await getAll('/trasporti/corse/', { data: dataISO });
      setCorseGiorno(corse);
    } catch (error) {
      console.error('Errore caricamento corse:', error);
      notify.error('Errore nel caricamento delle corse');
      setCorseGiorno([]);
    } finally {
      setCaricamentoGiorno(false);
    }
  };

  const apriGiorno = async (dataISO) => {
    // Ricliccare lo stesso giorno lo richiude
    if (giornoScelto === dataISO) {
      setGiornoScelto(null);
      setCorseGiorno([]);
      return;
    }
    setGiornoScelto(dataISO);
    await caricaCorseGiorno(dataISO);
  };

  const apriCorsa = async (corsa) => {
    setCaricamentoDettaglio(true);
    setDettaglio({ corsa, passeggeri: [], totale_saliti: 0, scansioni_respinte: 0 });
    try {
      const res = await api.get(`/trasporti/corse/${corsa.id}/passeggeri/`);
      setDettaglio(res.data);
    } catch (error) {
      console.error('Errore caricamento passeggeri:', error);
      notify.error('Errore nel caricamento dei passeggeri');
      setDettaglio(null);
    } finally {
      setCaricamentoDettaglio(false);
    }
  };

  // Chiusura di una singola corsa rimasta aperta
  const chiudiCorsa = async (corsa, e) => {
    e.stopPropagation();
    if (!window.confirm(
      `Chiudere la corsa ${corsa.linea_codice} del ${new Date(`${corsa.data}T12:00:00`)
        .toLocaleDateString('it-IT')}?`
    )) return;
    setInChiusura(corsa.id);
    const toastId = notify.loading('Chiusura in corso...');
    try {
      await api.post(`/trasporti/corse/${corsa.id}/chiudi/`);
      notify.update(toastId, { type: 'success', render: 'Corsa chiusa' });
      await caricaMese();
      await caricaCorseGiorno(giornoScelto);
    } catch (error) {
      notify.update(toastId, {
        type: 'error',
        render: error.response?.data?.errore || 'Errore nella chiusura',
      });
    } finally {
      setInChiusura(null);
    }
  };

  // Chiusura in blocco: prima si mostra cosa verrebbe fatto
  const chiudiTutte = async () => {
    const toastId = notify.loading('Controllo delle corse aperte...');
    try {
      const anteprima = await api.post('/trasporti/corse/chiudi_aperte/', { prova: true });
      const { corse_chiuse: n, doppioni_assorbiti: d } = anteprima.data;
      notify.dismiss(toastId);
      if (!n) {
        notify.info('Nessuna corsa rimasta aperta');
        return;
      }
      const messaggio = d > 0
        ? `Verranno chiuse ${n} corse, unendo ${d} partenze doppie dello stesso viaggio. Procedere?`
        : `Verranno chiuse ${n} corse rimaste aperte. Procedere?`;
      if (!window.confirm(messaggio)) return;

      const toast2 = notify.loading('Chiusura in corso...');
      const res = await api.post('/trasporti/corse/chiudi_aperte/', {});
      notify.update(toast2, {
        type: 'success',
        render: `${res.data.corse_chiuse} corse chiuse`
          + (res.data.doppioni_assorbiti ? `, ${res.data.doppioni_assorbiti} doppioni uniti` : ''),
      });
      await caricaMese();
      await caricaCorseGiorno(giornoScelto);
    } catch (error) {
      notify.dismiss(toastId);
      notify.error(error.response?.data?.errore || 'Errore nella chiusura');
    }
  };

  const unisciDuplicati = async () => {
    const n = duplicati?.doppioni_assorbiti || 0;
    if (!window.confirm(
      `Sono stati trovati ${duplicati.viaggi_uniti} viaggi partiti più volte. `
      + `Verranno unite ${n} partenze doppie, conservando l'orario di partenza `
      + 'e tutte le scansioni. Procedere?'
    )) return;
    const toastId = notify.loading('Unione in corso...');
    try {
      const res = await api.post('/trasporti/corse/unisci_duplicati/', {});
      notify.update(toastId, {
        type: 'success',
        render: `${res.data.viaggi_uniti} viaggi uniti, ${res.data.doppioni_assorbiti} doppioni rimossi`,
      });
      await caricaMese();
      await caricaCorseGiorno(giornoScelto);
      await contaDuplicati();
    } catch (error) {
      notify.update(toastId, {
        type: 'error',
        render: error.response?.data?.errore || 'Errore nell\'unione',
      });
    }
  };

  const perData = new Map(giorni.map((g) => [g.data, g]));

  // Griglia: si parte dal lunedì della settimana in cui cade il giorno 1
  const celle = [];
  const scarto = (primoGiorno.getDay() + 6) % 7;
  for (let i = 0; i < scarto; i += 1) celle.push(null);
  for (let d = 1; d <= ultimoGiorno.getDate(); d += 1) {
    celle.push(new Date(mese.getFullYear(), mese.getMonth(), d));
  }

  const totaleCorse = giorni.reduce((a, g) => a + g.corse, 0);
  const totalePasseggeri = giorni.reduce((a, g) => a + g.passeggeri, 0);
  const totaleAperte = giorni.reduce((a, g) => a + g.in_corso, 0);

  const cambiaMese = (delta) =>
    setMese(new Date(mese.getFullYear(), mese.getMonth() + delta, 1));

  const tratta = (c) => {
    const luoghi = (c.localita_servite_nomi || []).join(' · ');
    return luoghi || c.linea_nome || c.linea_codice || '—';
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Storico corse</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Calendario delle corse con autista, tratta e passeggeri
            </p>
          </div>
          <button
            onClick={() => setMese(new Date(oggi.getFullYear(), oggi.getMonth(), 1))}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:border-gray-900 transition"
          >
            <RotateCcw className="w-4 h-4" />
            Mese corrente
          </button>
        </div>

        {modificabile && duplicati?.viaggi_uniti > 0 && (
          <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-900">
                {duplicati.viaggi_uniti === 1
                  ? '1 viaggio risulta partito più volte'
                  : `${duplicati.viaggi_uniti} viaggi risultano partiti più volte`}
                {' '}({duplicati.doppioni_assorbiti} partenze doppie).
                {' '}Unendole resta una sola corsa, con l'orario di partenza e tutte le scansioni.
              </p>
            </div>
            <button
              onClick={unisciDuplicati}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-amber-400 text-amber-900 bg-white rounded-lg hover:border-amber-600 transition shrink-0"
            >
              <Merge className="w-3.5 h-3.5" />
              Unisci
            </button>
          </div>
        )}

        {/* Riepilogo del mese */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <p className="text-xs text-gray-500">Corse nel mese</p>
            <p className="text-2xl font-bold text-gray-900">{totaleCorse}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <p className="text-xs text-gray-500">Passeggeri trasportati</p>
            <p className="text-2xl font-bold text-gray-900">{totalePasseggeri}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-gray-500">Corse mai chiuse</p>
              <p className={`text-2xl font-bold ${totaleAperte > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                {totaleAperte}
              </p>
            </div>
            {modificabile && totaleAperte > 0 && (
              <button
                onClick={chiudiTutte}
                title="Chiude le corse rimaste aperte unendo le partenze doppie"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-amber-300 text-amber-800 bg-amber-50 rounded-lg hover:border-amber-500 transition shrink-0"
              >
                <CheckSquare className="w-3.5 h-3.5" />
                Chiudile
              </button>
            )}
          </div>
        </div>

        {/* Calendario */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <button
              onClick={() => cambiaMese(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
              aria-label="Mese precedente"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-gray-400" />
              {MESI[mese.getMonth()]} {mese.getFullYear()}
            </h2>
            <button
              onClick={() => cambiaMese(1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
              aria-label="Mese successivo"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="p-2 sm:p-3">
              <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1">
                {GIORNI.map((g) => (
                  <div key={g} className="text-center text-[11px] font-semibold text-gray-500 py-1">
                    {g}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {celle.map((data, i) => {
                  if (!data) return <div key={`v-${i}`} />;
                  const chiave = iso(data);
                  const info = perData.get(chiave);
                  const isOggi = chiave === iso(oggi);
                  const scelto = giornoScelto === chiave;
                  return (
                    <button
                      key={chiave}
                      onClick={() => info && apriGiorno(chiave)}
                      disabled={!info}
                      className={`min-h-[62px] sm:min-h-[78px] rounded-lg border p-1.5 text-left transition ${
                        scelto
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : info
                          ? 'border-gray-200 bg-white hover:border-gray-900 cursor-pointer'
                          : 'border-gray-100 bg-gray-50/60 cursor-default'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-xs font-bold ${
                            scelto ? 'text-white'
                              : isOggi ? 'text-blue-600'
                              : info ? 'text-gray-900' : 'text-gray-400'
                          }`}
                        >
                          {data.getDate()}
                        </span>
                        {info?.in_corso > 0 && (
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-amber-500"
                            title={`${info.in_corso} corse mai chiuse`}
                          />
                        )}
                      </div>
                      {info && (
                        <div className="mt-1 space-y-0.5">
                          <div
                            className={`flex items-center gap-1 text-[10px] font-semibold ${
                              scelto ? 'text-white' : 'text-gray-700'
                            }`}
                          >
                            <Bus className="w-3 h-3 shrink-0" />
                            {info.corse}
                          </div>
                          <div
                            className={`flex items-center gap-1 text-[10px] ${
                              scelto ? 'text-gray-200' : 'text-gray-500'
                            }`}
                          >
                            <Users className="w-3 h-3 shrink-0" />
                            {info.passeggeri}
                          </div>
                          <div
                            className={`hidden sm:block text-[9px] truncate ${
                              scelto ? 'text-gray-300' : 'text-gray-400'
                            }`}
                          >
                            {info.gruppi.join(', ')}
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Corse del giorno scelto */}
        {giornoScelto && (
          <div className="mt-5 bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">
                Corse del{' '}
                {new Date(`${giornoScelto}T12:00:00`).toLocaleDateString('it-IT', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                })}
              </h3>
              <button
                onClick={() => { setGiornoScelto(null); setCorseGiorno([]); }}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {caricamentoGiorno ? (
              <div className="py-10 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : corseGiorno.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-500">
                Nessuna corsa registrata in questa giornata
              </p>
            ) : (
              <div className="divide-y divide-gray-100">
                {corseGiorno.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => apriCorsa(c)}
                    className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                          c.tipo_corsa === 'ANDATA'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-violet-100 text-violet-700'
                        }`}
                      >
                        {c.tipo_corsa === 'ANDATA'
                          ? <ArrowRight className="w-4 h-4" />
                          : <ArrowLeft className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {c.linea_codice} · {c.tipo_corsa === 'ANDATA' ? 'Andata' : 'Ritorno'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {c.autista_nome || 'Autista non indicato'}
                          {c.mezzo_targa ? ` · ${c.mezzo_targa}` : ''}
                          {c.orario_partenza_effettivo
                            ? ` · ${c.orario_partenza_effettivo.slice(0, 5)}`
                            : ''}
                        </p>
                        <p className="text-[11px] text-gray-400 truncate">{tratta(c)}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      {c.stato === 'IN_CORSO' && (
                        modificabile ? (
                          <button
                            onClick={(e) => chiudiCorsa(c, e)}
                            disabled={inChiusura === c.id}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold bg-amber-100 text-amber-800 rounded-full hover:bg-amber-200 transition disabled:opacity-50"
                          >
                            {inChiusura === c.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <CheckSquare className="w-3 h-3" />}
                            chiudi
                          </button>
                        ) : (
                          <span className="px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-800 rounded-full">
                            mai chiusa
                          </span>
                        )
                      )}
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{c.passeggeri_totali}</p>
                        <p className="text-[10px] text-gray-500">passeggeri</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-300" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Passeggeri della corsa */}
      {dettaglio && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col">
            <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-start gap-3">
              <div className="min-w-0">
                <h2 className="text-base font-bold text-gray-900 truncate">
                  {dettaglio.corsa.linea_codice} ·{' '}
                  {dettaglio.corsa.tipo_corsa === 'ANDATA' ? 'Andata' : 'Ritorno'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {new Date(`${dettaglio.corsa.data}T12:00:00`).toLocaleDateString('it-IT', {
                    day: 'numeric', month: 'long', year: 'numeric'
                  })}
                  {' · '}
                  {dettaglio.corsa.autista_nome || 'autista non indicato'}
                  {dettaglio.corsa.mezzo_targa ? ` · ${dettaglio.corsa.mezzo_targa}` : ''}
                </p>
              </div>
              <button
                onClick={() => setDettaglio(null)}
                className="p-2 hover:bg-gray-100 rounded-lg shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500">Saliti a bordo</p>
                <p className="text-xl font-bold text-gray-900">{dettaglio.totale_saliti}</p>
                {dettaglio.saliti_senza_tessera > 0 && (
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {dettaglio.saliti_con_tessera} con tessera
                    {' · '}
                    {dettaglio.saliti_senza_tessera} senza
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500">Scansioni respinte</p>
                <p className={`text-xl font-bold ${
                  dettaglio.scansioni_respinte > 0 ? 'text-red-600' : 'text-gray-900'
                }`}>
                  {dettaglio.scansioni_respinte}
                </p>
              </div>
            </div>

            {(dettaglio.corsa.localita_servite_nomi || []).length > 0 && (
              <div className="px-4 py-2 border-b border-gray-200 flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                <p className="text-xs text-gray-600">
                  {dettaglio.corsa.localita_servite_nomi.join(' · ')}
                </p>
              </div>
            )}

            <div className="overflow-y-auto flex-1">
              {caricamentoDettaglio ? (
                <div className="py-12 flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : dettaglio.passeggeri.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <p className="text-sm text-gray-500">Nessun tesserino scansionato</p>
                  {dettaglio.corsa.passeggeri_totali > 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      A fine corsa erano stati indicati {dettaglio.corsa.passeggeri_totali} passeggeri
                    </p>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {dettaglio.passeggeri.map((p) => (
                    <div key={p.evento_id} className="px-4 py-2.5 flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                          p.esito ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {p.esito ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {p.nome}
                          {p.ripetuta && (
                            <span className="ml-2 text-[10px] font-normal text-gray-400">
                              (già salito)
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                          <UserCircle className="w-3 h-3 shrink-0" />
                          {p.abbonamento_codice}
                          {p.scuola && (
                            <>
                              <School className="w-3 h-3 shrink-0 ml-1" />
                              <span className="truncate">{p.scuola}</span>
                            </>
                          )}
                        </p>
                        {!p.esito && (
                          <p className="text-xs text-red-600 mt-0.5">
                            {ETICHETTE_MOTIVO[p.motivo] || p.motivo}
                          </p>
                        )}
                        {/* Scritta alla scansione: il tesserino era gia'
                            su un altro mezzo. Diverso da "ripetuta", che
                            e' il ripasso dentro questa stessa corsa. */}
                        {p.nota && (
                          <p className="text-xs text-orange-700 mt-0.5">
                            {p.nota}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
                        <Clock className="w-3 h-3" />
                        {p.orario}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default StoricoCorse;
